import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useAutoLogout } from '../hooks/useAutoLogout'
import AutoLogoutWarning from './AutoLogoutWarning'
import { useAuthStore } from '../store/authStore'

interface AutoLogoutProviderProps {
  children: React.ReactNode
  timeoutMinutes?: number
  warningMinutes?: number
  enabled?: boolean
}

export default function AutoLogoutProvider({
  children,
  timeoutMinutes = 15,
  warningMinutes = 1,
  enabled = true
}: AutoLogoutProviderProps) {
  const [showWarning, setShowWarning] = useState(false)
  const navigate = useNavigate()
  const { user, logoutDueToInactivity } = useAuthStore()

  // Debug logging for production
  console.log('🔐 AutoLogoutProvider initialized:', {
    timeoutMinutes,
    warningMinutes,
    enabled,
    hasUser: !!user,
    isProduction: !import.meta.env.DEV
  })

  const handleWarning = useCallback(() => {
    if (!enabled) return
    setShowWarning(true)
  }, [enabled])

  const handleLogout = useCallback(() => {
    setShowWarning(false)
    logoutDueToInactivity()
    toast.error('You\'ve been logged out due to inactivity.')
    navigate('/login')
  }, [navigate, logoutDueToInactivity])

  const handleStaySignedIn = useCallback(() => {
    setShowWarning(false)
    toast.success('Session extended successfully!')
  }, [])

  const handleLogoutNow = useCallback(() => {
    setShowWarning(false)
    logoutDueToInactivity()
    toast.info('You have been logged out.')
    navigate('/login')
  }, [navigate, logoutDueToInactivity])

  const { extendSession, logoutNow } = useAutoLogout({
    timeoutMinutes,
    warningMinutes,
    onWarning: handleWarning,
    onLogout: handleLogout,
    enabled: enabled && !!user // Only enable for authenticated users
  })

  const onStaySignedIn = useCallback(() => {
    extendSession()
    handleStaySignedIn()
  }, [extendSession, handleStaySignedIn])

  const onLogoutNow = useCallback(() => {
    logoutNow()
    handleLogoutNow()
  }, [logoutNow, handleLogoutNow])

  return (
    <>
      {children}
      <AutoLogoutWarning
        isOpen={showWarning}
        onStaySignedIn={onStaySignedIn}
        onLogoutNow={onLogoutNow}
        warningMinutes={warningMinutes}
      />
    </>
  )
} 